import React, { useEffect, useContext, useState, useMemo, useRef, useCallback } from 'react'
import { StatusText } from './style'

import { Eye, Pencil, Trash2, Copy } from 'lucide-react'
import { FreightContext, Freight } from '../../../../contexts/FreightContext'
import axios from '../../../../config/axios'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { RouterOutletType } from '../index2'
import SectionContext from '../../../../contexts/SectionContext'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import TableFiltersContext from '../../../../contexts/TableFiltersContext'
import { OverflowCell } from '../../../../components/OverflowCell'
import styled from 'styled-components'
import {
  StandardDataTable,
  useColumnsState,
  StandardColumn,
} from '../../../../components/StandardPage'

const freightScreenKey = 'router_freight'

const TableRoot = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;

  .duplicated-row {
    background-color: #fffbe3;
  }
`

const dataFormatter = (isoDateString: string): string => {
  if (!isoDateString) {
    return ''
  }

  const dateUtc = new Date(isoDateString)

  if (isNaN(dateUtc.getTime())) {
    console.error('Data ISO inválida recebida:', isoDateString)
    return ''
  }

  const year = dateUtc.getUTCFullYear()
  const month = dateUtc.getUTCMonth() + 1
  const day = dateUtc.getUTCDate()

  const formattedMonth = month < 10 ? `0${month}` : month
  const formattedDay = day < 10 ? `0${day}` : day

  return `${formattedDay}/${formattedMonth}/${year}`
}

const dateHourFormatter = (isoDateString: string) => {
  const date = new Date(isoDateString)

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()

  const brasiliaOffset = -3
  const utcHours = date.getUTCHours()
  const brasiliaHours = utcHours + brasiliaOffset

  const finalHours = brasiliaHours < 0 ? 24 + brasiliaHours : brasiliaHours
  const finalMinutes = String(date.getUTCMinutes()).padStart(2, '0')

  return `dia ${day}/${month}/${year} às ${String(finalHours).padStart(
    2,
    '0',
  )}:${finalMinutes}`
}

const mapStatus = (status: string): string => {
  if (status === 'A programar') return ''
  if (status === 'Rota programada') return 'pending'
  if (status === 'Endereço pendente' || status === 'Cancelado') return 'cancel'
  if (status === 'Não Entregue') return 'notDelivered'
  if (status === 'Entregue' || status === 'Coletado') return 'completed'
  return status
}

const formatReadyToShip = (freight: Freight): string => {
  if (!freight.readyToShip) return ''
  return new Date(freight.readyToShip).setUTCHours(0, 0, 0, 0) <=
    new Date().setUTCHours(0, 0, 0, 0)
    ? 'Liberado'
    : dataFormatter(freight.readyToShip)
}

const formatObservation = (freight: Freight): string =>
  freight.observation ? freight.observation.replace(/\[CÓPIA\]/gi, '').trim() : ''

const formatDeliveryAddress = (freight: Freight): string => {
  const addr = freight.Address
  if (!addr) return '-'
  const parts: string[] = []
  if (addr.street) {
    let streetPart = addr.street
    if (addr.number) streetPart += `, ${addr.number}`
    if (addr.complement) streetPart += ` - ${addr.complement}`
    parts.push(streetPart)
  }
  if (addr.Neighborhood?.name) {
    parts.push(addr.Neighborhood.name)
  }
  if (addr.Neighborhood?.City?.name) {
    const cityState = addr.Neighborhood.City.State?.uf
      ? `${addr.Neighborhood.City.name}/${addr.Neighborhood.City.State.uf}`
      : addr.Neighborhood.City.name
    parts.push(cityState)
  }
  if (addr.cep) {
    parts.push(`CEP: ${addr.cep}`)
  }
  return parts.length > 0 ? parts.join(' - ') : '-'
}

const formatLoad = (freight: Freight): string => {
  const load = freight.Load
  if (!load) return '-'
  const parts: string[] = []
  if (load.weight) parts.push(`Peso: ${load.weight} kg`)
  if (load.cubage) parts.push(`Cubagem: ${load.cubage} m³`)
  const dims: string[] = []
  if (load.length) dims.push(`C: ${load.length}`)
  if (load.width) dims.push(`L: ${load.width}`)
  if (load.height) dims.push(`A: ${load.height}`)
  if (dims.length > 0) parts.push(`Dimensões: ${dims.join('x')}`)
  if (load.detail) parts.push(load.detail)
  return parts.length > 0 ? parts.join(' | ') : '-'
}

export interface FreightTableRow extends Freight {
  isGroupParent?: boolean
  groupCount?: number
  subRowLabel?: string
  subRows?: FreightTableRow[]
  groupKey?: string
  formattedLoad?: string
  displayCompanyName?: string
}

export const getAddressGroupKey = (freight: Freight): string => {
  const addr = freight.Address
  if (!addr || !addr.street) return `single-${freight.id || Math.random()}`
  const street = (addr.street || '').trim().toLowerCase()
  const number = (addr.number || '').trim().toLowerCase()
  const neighborhood = (addr.Neighborhood?.name || '').trim().toLowerCase()
  const city = (addr.Neighborhood?.City?.name || '').trim().toLowerCase()
  const uf = (addr.Neighborhood?.City?.State?.uf || '').trim().toLowerCase()
  const cep = String(addr.cep || '').replace(/\D/g, '')
  return `${cep}_${street}_${number}_${neighborhood}_${city}_${uf}`
}

export const isDeliveredFreight = (freight: Freight): boolean => {
  const status = (freight.status || '').trim()
  return status === 'Entregue' || status === 'Coletado' || status === 'Concluído'
}

export const getFreightGroupKey = (freight: Freight): string => {
  const addressKey = getAddressGroupKey(freight)
  const isDelivered = isDeliveredFreight(freight)
  return `${addressKey}__${isDelivered ? 'delivered' : 'pending'}`
}

const buildGroupedFreightRows = (list: Freight[]): FreightTableRow[] => {
  const groupsMap = new Map<string, Freight[]>()

  list.forEach((f) => {
    const key = getFreightGroupKey(f)
    if (!groupsMap.has(key)) {
      groupsMap.set(key, [])
    }
    groupsMap.get(key)!.push(f)
  })

  const result: FreightTableRow[] = []

  groupsMap.forEach((group, key) => {
    if (group.length === 1) {
      result.push({
        ...group[0],
        isGroupParent: false,
      })
    } else {
      const first = group[0]
      const companies = Array.from(
        new Set(group.map((f) => f.Company?.companyName).filter(Boolean)),
      )
      const displayCompanyName =
        companies.length === 1
          ? `${companies[0]} (${group.length})`
          : `${group.length} fretes (${companies.join(', ')})`

      const orderNumbers =
        group
          .map((f) => f.orderNumber)
          .filter(Boolean)
          .join(' / ') || '-'
      const types = Array.from(new Set(group.map((f) => f.type).filter(Boolean)))
      const displayType = types.length === 1 ? types[0] : 'Múltiplos'
      const statuses = Array.from(
        new Set(group.map((f) => f.status).filter(Boolean)),
      )
      const displayStatus = statuses.length === 1 ? statuses[0] : 'Múltiplos'

      let totalWeight = 0
      let totalCubage = 0
      group.forEach((f) => {
        if (f.Load?.weight) {
          const w = parseFloat(String(f.Load.weight).replace(',', '.'))
          if (!isNaN(w)) totalWeight += w
        }
        if (f.Load?.cubage) {
          const c = parseFloat(String(f.Load.cubage).replace(',', '.'))
          if (!isNaN(c)) totalCubage += c
        }
      })

      const loadParts: string[] = []
      if (totalWeight > 0)
        loadParts.push(
          `Peso: ${totalWeight.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} kg`,
        )
      if (totalCubage > 0)
        loadParts.push(
          `Cubagem: ${totalCubage.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} m³`,
        )
      const formattedLoad = loadParts.length > 0 ? loadParts.join(' | ') : '-'

      const allReady = group.every((f) => {
        if (!f.readyToShip) return false
        return (
          new Date(f.readyToShip).setUTCHours(0, 0, 0, 0) <=
          new Date().setUTCHours(0, 0, 0, 0)
        )
      })

      const subRows: FreightTableRow[] = group.map((f, i) => ({
        ...f,
        subRowLabel: `${i + 1}/${group.length}`,
        isGroupParent: false,
      }))

      result.push({
        ...first,
        id: -Math.abs(
          key.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) ||
            Math.floor(Math.random() * 100000),
        ),
        isGroupParent: true,
        groupCount: group.length,
        groupKey: key,
        displayCompanyName,
        orderNumber: orderNumbers,
        type: displayType,
        status: displayStatus,
        formattedLoad,
        readyToShip: allReady ? 'Liberado' : first.readyToShip,
        subRows,
      })
    }
  })

  return result
}

const buildFreightColumns = (): StandardColumn<FreightTableRow>[] => [
  {
    id: 'companyName',
    label: 'Cliente',
    accessor: (freight) =>
      freight.isGroupParent
        ? freight.displayCompanyName || freight.Company?.companyName || '-'
        : freight.Company?.companyName || '-',
    cell: (freight) => {
      if (freight.subRowLabel) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              style={{
                fontWeight: 600,
                color: '#0090D3',
                fontSize: '12px',
                minWidth: '24px',
              }}
            >
              {freight.subRowLabel}
            </span>
            <OverflowCell
              text={freight.Company?.companyName || '-'}
              maxWidth={140}
            />
          </div>
        )
      }
      if (freight.isGroupParent) {
        return (
          <OverflowCell
            text={
              freight.displayCompanyName ||
              freight.Company?.companyName ||
              '-'
            }
            maxWidth={160}
          />
        )
      }
      return (
        <OverflowCell
          text={freight.Company?.companyName || '-'}
          maxWidth={160}
        />
      )
    },
  },
  {
    id: 'orderNumber',
    label: 'Numero de venda',
    accessor: (freight) => freight.orderNumber || '-',
    cell: (freight) => (
      <OverflowCell text={freight.orderNumber || '-'} maxWidth={140} />
    ),
  },
  { id: 'type', label: 'Tipo', accessor: (freight) => freight.type },
  {
    id: 'status',
    label: 'Status',
    accessor: (freight) => freight.status,
    cell: (freight) => (
      <StatusText className={mapStatus(freight.status)}>
        {freight.status}
      </StatusText>
    ),
  },
  {
    id: 'deliveryAddress',
    label: 'Endereço de entrega',
    accessor: formatDeliveryAddress,
    cell: (freight) => (
      <OverflowCell text={formatDeliveryAddress(freight)} maxWidth={220} />
    ),
  },
  {
    id: 'load',
    label: 'Carga',
    accessor: (freight) =>
      freight.isGroupParent ? freight.formattedLoad || '-' : formatLoad(freight),
    cell: (freight) => (
      <OverflowCell
        text={
          freight.isGroupParent
            ? freight.formattedLoad || '-'
            : formatLoad(freight)
        }
        maxWidth={200}
      />
    ),
  },
  {
    id: 'readyToShip',
    label: 'Liberado pra expedir',
    accessor: formatReadyToShip,
  },
  {
    id: 'readyToShipDate',
    label: 'Data liberada para expedir',
    accessor: (freight) =>
      freight.readyToShip ? dataFormatter(freight.readyToShip) : '',
  },
  {
    id: 'deadLine',
    label: 'Data limite de entrega',
    accessor: (freight) =>
      freight.deadLine ? dataFormatter(freight.deadLine) : '',
  },
  {
    id: 'observation',
    label: 'Observações',
    accessor: formatObservation,
    cell: (freight) => (
      <OverflowCell text={formatObservation(freight)} maxWidth={150} />
    ),
  },
]

interface FreightTableType {
  onChange?: () => void
}

const FreightTable: React.FC<FreightTableType> = () => {
  const { setFreight } = useContext(FreightContext)
  const { setRouterSection } = useContext(SectionContext)
  const [freights, setFreights] = useState<Freight[]>([])
  const [totalFreights, setTotalFreights] = useState<number | null>(null)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const { setFeedback } = useContext(FeedbackContext)
  const navigate = useNavigate()
  const { search, appliedFilters, setTabIndex, refresh, setLoadingHeader, viewMode = 'standard' } =
    useOutletContext<RouterOutletType>() || {}
  const { freightStatusFilter } = useContext(TableFiltersContext)
  const [duplicatedIds, setDuplicatedIds] = useState<number[]>([])
  const isFirstRender = useRef(true)

  const columns = useMemo(() => buildFreightColumns(), [])
  const { columnsState, handleColumnsChange, handleRestoreColumns } =
    useColumnsState(freightScreenKey, columns)

  useEffect(() => {
    const idsRaw = sessionStorage.getItem('duplicatedFreights')
    const ids = idsRaw ? JSON.parse(idsRaw) : []

    if (ids.length > 0) {
      setDuplicatedIds(ids)
    }
    sessionStorage.removeItem('duplicatedFreights')
  }, [freightStatusFilter, search])

  const handleEditFreight = async (id: number) => {
    setFreight(await getFreight(id))
    navigate('create')
  }

  const handleViewFreight = async (id: number) => {
    setFreight(await getFreight(id))
    navigate(`${id}`)
  }

  const getFreight = async (id: number): Promise<Freight> => {
    const response = await axios.get(
      `${process.env.REACT_APP_GATEWAY_BASE_URL}/router/freight/${id}`,
    )
    return response.data as Freight
  }

  const getFreights = async (
    pageNumber: number,
    statusFilter: string | null = null,
    search: string | null = null,
  ) => {
    try {
      const token = localStorage.getItem('token')
      let url = `${process.env.REACT_APP_GATEWAY_BASE_URL}/router/freight?page=${pageNumber}&size=50`
      if (statusFilter && statusFilter !== 'Todos') {
        url += `&status=${encodeURIComponent(statusFilter)}`
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`
      }
      if (appliedFilters && appliedFilters.length > 0) {
        url += `&filters=${encodeURIComponent(JSON.stringify(appliedFilters))}`
      }
      const response = await axios.get(url,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      )
      try {
        let countUrl = `${process.env.REACT_APP_GATEWAY_BASE_URL}/router/route/freights/count?1=1`
        if (statusFilter && statusFilter !== 'Todos') {
          countUrl += `&status=${encodeURIComponent(statusFilter)}`
        }
        if (search) {
          countUrl += `&search=${encodeURIComponent(search)}`
        }
        if (appliedFilters && appliedFilters.length > 0) {
          countUrl += `&filters=${encodeURIComponent(JSON.stringify(appliedFilters))}`
        }
        const countResp = await axios.get(countUrl, { headers: { Authorization: `Bearer ${token}` } })
        setTotalFreights(countResp.data?.total ?? null)
      } catch (err) {
        setTotalFreights(null)
      }
      return response.data as Freight[]
    } catch (error) {
      console.error('Erro ao buscar fretes:', error)
      setFeedback({
        isOpen: true,
        message: 'Erro ao carregar fretes!',
        statusOk: false,
      })
      return []
    }
  }

  const deleteFreight = async (id: number) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/router/freight/${id}`,
      )
      setFreights((prevFreights) =>
        prevFreights.filter((freight) => freight.id !== id),
      )
      setFeedback({
        isOpen: true,
        message: 'Frete deletado!',
        statusOk: true,
      })
    } catch {
      setFeedback({
        isOpen: true,
        message: 'Erro ao deletar frete',
        statusOk: false,
      })
    }
  }

  const duplicateFreight = async (id: number) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/router/freight/${id}/duplicate`,
      )
      setFeedback({
        isOpen: true,
        message: 'Frete duplicado!',
        statusOk: true,
      })
      const newFreightId = response.data.id
      setDuplicatedIds((prevIds) =>
        prevIds.includes(newFreightId) ? prevIds : [...prevIds, newFreightId],
      )
      fetchInitialFreights()
    } catch {
      setFeedback({
        isOpen: true,
        message: 'Erro ao duplicar frete',
        statusOk: false,
      })
    }
  }

  const canDuplicateFreight = (freight: Freight) => {
    const isCompleted =
      freight.status === 'Coletado' || freight.status === 'Entregue'
    const isAlreadyDuplicate = freight.observation
      ?.toUpperCase()
      .includes('[CÓPIA]')
    return !isCompleted && !isAlreadyDuplicate
  }

  const loadMoreFreights = async () => {
    if (!hasMore) {
      return
    }

    try {
      const nextPage = page + 1
      const newFreights = await getFreights(
        nextPage,
        freightStatusFilter,
        search,
      )

      if (newFreights.length === 0 || newFreights.length < 50) {
        setHasMore(false)
      }
      setFreights((prevFreights) => [...prevFreights, ...newFreights])
      setPage(nextPage)
    } catch (error) {
      setFeedback({
        isOpen: true,
        message: 'Erro ao carregar mais fretes',
        statusOk: false,
      })
    }
  }

  const fetchInitialFreights = useCallback(async () => {
    setLoadingHeader?.(true)
    try {
      setPage(0)
      setHasMore(true)
      const initialFreights = await getFreights(
        0,
        freightStatusFilter,
        search,
      )
      setFreights(initialFreights)
      if (initialFreights.length < 50) {
        setHasMore(false)
      }
    } catch (error) {
      setFeedback({
        isOpen: true,
        message: 'Erro ao carregar fretes iniciais',
        statusOk: false,
      })
      setFreights([])
    } finally {
      setLoadingHeader?.(false)
    }
  }, [freightStatusFilter, search, appliedFilters, setFeedback, setLoadingHeader])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (refresh) fetchInitialFreights()
  }, [refresh])

  useEffect(() => {
    fetchInitialFreights()
  }, [fetchInitialFreights])

  useEffect(() => {
    setTabIndex(0)
    setRouterSection('freight')
  }, [])

  const tableData = useMemo(() => {
    if (viewMode === 'standard') {
      return freights.map((f) => ({
        ...f,
        isGroupParent: false,
        subRows: undefined,
      }))
    }
    return buildGroupedFreightRows(freights)
  }, [freights, viewMode])

  return (
    <TableRoot>
      <StandardDataTable<FreightTableRow>
        tableName="Freight"
        columns={columns}
        data={tableData}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        totalCount={totalFreights}
        onLoadMore={loadMoreFreights}
        hasMore={hasMore}
        expandColumnId={viewMode === 'grouped' ? 'companyName' : undefined}
        getSubRows={(row) => row.subRows}
        getRowId={(row) =>
          String(row.subRowLabel ? `${row.id}-${row.subRowLabel}` : row.id)
        }
        countLabel='fretes'
        exportFileName='fretes'
        emptyMessage='Nenhum frete encontrado.'
        rowClassName={(freight) =>
          duplicatedIds.includes(freight.id!) ? 'duplicated-row' : ''
        }
        rowTitle={(freight) =>
          freight.isGroupParent
            ? `${freight.groupCount} fretes agrupados para este endereço`
            : `Criado${freight.CreatedByUser ? ' por: ' + freight.CreatedByUser.name : ''} ${
                freight.createdAt ? dateHourFormatter(freight.createdAt) : ''
              }`
        }
        rowActions={(freight) => {
          if (freight.isGroupParent) return []
          return [
            {
              label: 'Visualizar',
              icon: <Eye size={16} />,
              onClick: () => handleViewFreight(freight.id!),
            },
            {
              label: 'Editar',
              icon: <Pencil size={16} />,
              onClick: () => handleEditFreight(freight.id!),
            },
            ...(canDuplicateFreight(freight)
              ? [
                  {
                    label: 'Duplicar frete',
                    icon: <Copy size={16} />,
                    onClick: () => duplicateFreight(freight.id!),
                  },
                ]
              : []),
            {
              label: 'Excluir',
              icon: <Trash2 size={16} />,
              onClick: () => deleteFreight(freight.id!),
            },
          ]
        }}
      />
    </TableRoot>
  )
}

export default FreightTable
